import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Plus, Trash2, Edit2, Send, User, Building2, FileText, AlertTriangle,
  Clock, CheckCircle, Shield, Scale, DollarSign, ArrowRight, PenTool
} from 'lucide-react';
import { toast } from 'sonner';

// ========== EXECUÇÃO INTERNA ==========
export const InternalExecutionTab = ({ actId }) => {
  const { api, user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingTask, setEditingTask] = useState(null);
  const [form, setForm] = useState({ unidade_responsavel: '', responsavel_user_id: '', descricao: '', prazo_interno: '' });

  const canEdit = ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'CONTROLADORIA_INTERNA', 'SECRETARIA_EXECUTORA'].includes(user?.role);

  const fetchTasks = async () => {
    try {
      const res = await api.get(`/acts/${actId}/internal-tasks`);
      setTasks(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchTasks(); }, [actId]);

  const handleSubmit = async () => {
    try {
      if (editingTask) {
        await api.put(`/internal-tasks/${editingTask.task_id}`, form);
        toast.success('Tarefa atualizada');
      } else {
        await api.post(`/acts/${actId}/internal-tasks`, form);
        toast.success('Tarefa criada');
      }
      setShowDialog(false);
      setEditingTask(null);
      setForm({ unidade_responsavel: '', responsavel_user_id: '', descricao: '', prazo_interno: '' });
      fetchTasks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleDelete = async (taskId) => {
    if (!confirm('Confirma exclusão?')) return;
    try {
      await api.delete(`/internal-tasks/${taskId}`);
      toast.success('Tarefa excluída');
      fetchTasks();
    } catch (e) { toast.error('Erro ao excluir'); }
  };

  const handleStatusChange = async (taskId, newStatus) => {
    try {
      await api.put(`/internal-tasks/${taskId}`, { status: newStatus });
      toast.success('Status atualizado');
      fetchTasks();
    } catch (e) { toast.error('Erro'); }
  };

  const STATUS_COLORS = {
    PENDENTE: 'bg-yellow-100 text-yellow-800',
    EM_ANDAMENTO: 'bg-blue-100 text-blue-800',
    CONCLUIDO: 'bg-green-100 text-green-800',
    CANCELADO: 'bg-slate-100 text-slate-800'
  };

  if (loading) return <div className="p-4 text-center">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Tarefas Internas</h3>
        {canEdit && (
          <Button size="sm" onClick={() => { setEditingTask(null); setForm({ unidade_responsavel: '', responsavel_user_id: '', descricao: '', prazo_interno: '' }); setShowDialog(true); }}>
            <Plus className="w-4 h-4 mr-1" /> Nova Tarefa
          </Button>
        )}
      </div>

      {tasks.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhuma tarefa interna cadastrada</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {tasks.map((task) => (
            <Card key={task.task_id}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium">{task.descricao}</p>
                    <div className="flex gap-4 mt-2 text-sm text-slate-600">
                      <span className="flex items-center gap-1"><Building2 className="w-4 h-4" /> {task.unidade_responsavel}</span>
                      <span className="flex items-center gap-1"><Clock className="w-4 h-4" /> {task.prazo_interno}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Select value={task.status} onValueChange={(v) => handleStatusChange(task.task_id, v)} disabled={!canEdit}>
                      <SelectTrigger className={`w-36 h-8 text-xs ${STATUS_COLORS[task.status]}`}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="PENDENTE">Pendente</SelectItem>
                        <SelectItem value="EM_ANDAMENTO">Em Andamento</SelectItem>
                        <SelectItem value="CONCLUIDO">Concluído</SelectItem>
                        <SelectItem value="CANCELADO">Cancelado</SelectItem>
                      </SelectContent>
                    </Select>
                    {canEdit && (
                      <>
                        <Button size="icon" variant="ghost" onClick={() => { setEditingTask(task); setForm(task); setShowDialog(true); }}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="icon" variant="ghost" className="text-red-600" onClick={() => handleDelete(task.task_id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTask ? 'Editar Tarefa' : 'Nova Tarefa Interna'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Descrição</Label>
              <Textarea value={form.descricao} onChange={(e) => setForm({ ...form, descricao: e.target.value })} />
            </div>
            <div>
              <Label>Unidade Responsável</Label>
              <Input value={form.unidade_responsavel} onChange={(e) => setForm({ ...form, unidade_responsavel: e.target.value })} />
            </div>
            <div>
              <Label>ID do Responsável</Label>
              <Input value={form.responsavel_user_id} onChange={(e) => setForm({ ...form, responsavel_user_id: e.target.value })} />
            </div>
            <div>
              <Label>Prazo Interno</Label>
              <Input type="date" value={form.prazo_interno} onChange={(e) => setForm({ ...form, prazo_interno: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit}>{editingTask ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== ENCAMINHAMENTOS ==========
export const ForwardsTab = ({ actId }) => {
  const { api, user } = useAuth();
  const [forwards, setForwards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ destino_unit_id: '', despacho: '' });

  const canForward = ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'CONTROLADORIA_INTERNA', 'PROCURADORIA_JURIDICO', 'SECRETARIA_EXECUTORA'].includes(user?.role);

  const fetchForwards = async () => {
    try {
      const res = await api.get(`/acts/${actId}/forwards`);
      setForwards(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchForwards(); }, [actId]);

  const handleSubmit = async () => {
    try {
      await api.post(`/acts/${actId}/forward`, form);
      toast.success('Ato encaminhado com sucesso');
      setShowDialog(false);
      setForm({ destino_unit_id: '', despacho: '' });
      fetchForwards();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro ao encaminhar'); }
  };

  if (loading) return <div className="p-4 text-center">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Histórico de Encaminhamentos</h3>
        {canForward && (
          <Button size="sm" onClick={() => setShowDialog(true)}>
            <Send className="w-4 h-4 mr-1" /> Encaminhar
          </Button>
        )}
      </div>

      {forwards.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum encaminhamento registrado</CardContent></Card>
      ) : (
        <div className="relative">
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-slate-200" />
          <div className="space-y-4">
            {forwards.map((fwd, i) => (
              <div key={fwd.forward_id} className="relative pl-10">
                <div className="absolute left-2.5 w-3 h-3 rounded-full bg-blue-500 border-2 border-white" />
                <Card>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <span className="text-slate-600">{fwd.origem_unit_id}</span>
                      <ArrowRight className="w-4 h-4 text-blue-500" />
                      <span className="text-blue-700">{fwd.destino_unit_id}</span>
                    </div>
                    <p className="mt-2 text-slate-700">{fwd.despacho}</p>
                    <p className="mt-2 text-xs text-slate-500">
                      Por {fwd.created_by_name} em {new Date(fwd.created_at).toLocaleString('pt-BR')}
                    </p>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Encaminhar Ato</DialogTitle>
            <DialogDescription>Defina a unidade de destino e o despacho</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Unidade de Destino</Label>
              <Input value={form.destino_unit_id} onChange={(e) => setForm({ ...form, destino_unit_id: e.target.value })} placeholder="Ex: Secretaria de Fazenda" />
            </div>
            <div>
              <Label>Despacho</Label>
              <Textarea value={form.despacho} onChange={(e) => setForm({ ...form, despacho: e.target.value })} placeholder="Descreva o motivo do encaminhamento..." rows={4} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit}>Encaminhar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== RESPONSÁVEL FORMAL ==========
export const FormalResponsibleSection = ({ actId }) => {
  const { api, user } = useAuth();
  const [responsible, setResponsible] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ cpf: '', nome: '', cargo: '', matricula: '', orgao: '' });

  const canEdit = ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'CONTROLADORIA_INTERNA'].includes(user?.role);

  const fetchResponsible = async () => {
    try {
      const res = await api.get(`/acts/${actId}/responsible`);
      setResponsible(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchResponsible(); }, [actId]);

  const handleSubmit = async () => {
    try {
      await api.post(`/acts/${actId}/responsible`, form);
      toast.success('Responsável formal definido');
      setShowDialog(false);
      fetchResponsible();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  if (loading) return null;

  return (
    <Card className="border-l-4 border-l-amber-500">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <User className="w-4 h-4" /> Responsável Formal
        </CardTitle>
      </CardHeader>
      <CardContent>
        {responsible ? (
          <div className="space-y-1 text-sm">
            <p><strong>Nome:</strong> {responsible.nome}</p>
            <p><strong>CPF:</strong> {responsible.cpf}</p>
            <p><strong>Cargo:</strong> {responsible.cargo}</p>
            {responsible.matricula && <p><strong>Matrícula:</strong> {responsible.matricula}</p>}
            <p><strong>Órgão:</strong> {responsible.orgao}</p>
            <p className="text-xs text-slate-500 mt-2">
              Atribuído por {responsible.atribuido_por_nome} em {new Date(responsible.atribuido_em).toLocaleString('pt-BR')}
            </p>
            {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => { setForm(responsible); setShowDialog(true); }}>Alterar</Button>}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm mb-2">Nenhum responsável formal definido</p>
            {canEdit && <Button size="sm" onClick={() => setShowDialog(true)}>Definir Responsável</Button>}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Definir Responsável Formal</DialogTitle>
              <DialogDescription>Responsabilização com CPF conforme exigência legal</DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div><Label>CPF</Label><Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" /></div>
                <div><Label>Matrícula</Label><Input value={form.matricula} onChange={(e) => setForm({ ...form, matricula: e.target.value })} /></div>
              </div>
              <div><Label>Nome Completo</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} /></div>
              <div><Label>Cargo</Label><Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} /></div>
              <div><Label>Órgão</Label><Input value={form.orgao} onChange={(e) => setForm({ ...form, orgao: e.target.value })} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// ========== PARECER JURÍDICO ==========
export const LegalOpinionsTab = ({ actId }) => {
  const { api, user } = useAuth();
  const [opinions, setOpinions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editingOpinion, setEditingOpinion] = useState(null);
  const [form, setForm] = useState({ texto: '', status: 'RASCUNHO' });

  const canEdit = ['JURIDICO', 'PROCURADORIA_JURIDICO'].includes(user?.role);

  const fetchOpinions = async () => {
    try {
      const res = await api.get(`/acts/${actId}/legal-opinions`);
      setOpinions(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchOpinions(); }, [actId]);

  const handleSubmit = async () => {
    try {
      if (editingOpinion) {
        await api.put(`/legal-opinions/${editingOpinion.opinion_id}`, form);
        toast.success('Parecer atualizado');
      } else {
        await api.post(`/acts/${actId}/legal-opinions`, form);
        toast.success('Parecer criado');
      }
      setShowDialog(false);
      setEditingOpinion(null);
      setForm({ texto: '', status: 'RASCUNHO' });
      fetchOpinions();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  if (loading) return <div className="p-4 text-center">Carregando...</div>;

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-semibold">Pareceres Jurídicos</h3>
        {canEdit && (
          <Button size="sm" onClick={() => { setEditingOpinion(null); setForm({ texto: '', status: 'RASCUNHO' }); setShowDialog(true); }}>
            <Scale className="w-4 h-4 mr-1" /> Novo Parecer
          </Button>
        )}
      </div>

      {opinions.length === 0 ? (
        <Card><CardContent className="p-8 text-center text-slate-500">Nenhum parecer jurídico registrado</CardContent></Card>
      ) : (
        <div className="space-y-3">
          {opinions.map((op) => (
            <Card key={op.opinion_id} className={op.status === 'FINAL' ? 'border-l-4 border-l-green-500' : ''}>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <Badge variant={op.status === 'FINAL' ? 'default' : 'secondary'}>{op.status === 'FINAL' ? 'Final' : 'Rascunho'}</Badge>
                      <span className="text-sm text-slate-500">Por {op.user_name}</span>
                    </div>
                    <p className="text-slate-700 whitespace-pre-wrap">{op.texto}</p>
                    <p className="mt-2 text-xs text-slate-500">
                      Criado em {new Date(op.created_at).toLocaleString('pt-BR')}
                      {op.updated_at && ` • Atualizado em ${new Date(op.updated_at).toLocaleString('pt-BR')}`}
                    </p>
                  </div>
                  {canEdit && op.status !== 'FINAL' && (
                    <Button size="icon" variant="ghost" onClick={() => { setEditingOpinion(op); setForm({ texto: op.texto, status: op.status }); setShowDialog(true); }}>
                      <Edit2 className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingOpinion ? 'Editar Parecer' : 'Novo Parecer Jurídico'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Parecer</Label>
              <Textarea value={form.texto} onChange={(e) => setForm({ ...form, texto: e.target.value })} rows={10} placeholder="Digite o parecer jurídico..." />
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="RASCUNHO">Rascunho</SelectItem>
                  <SelectItem value="FINAL">Final (vinculante)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit}>{editingOpinion ? 'Salvar' : 'Criar'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ========== ASSINATURAS / CIÊNCIAS (COM ICP-BRASIL) ==========
export const AcknowledgementsSection = ({ actId }) => {
  const { api, user } = useAuth();
  const [acks, setAcks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showICPDialog, setShowICPDialog] = useState(false);
  const [form, setForm] = useState({ tipo: 'CIENCIA', via: 'CREDENCIAL_INTERNA', cpf: '' });
  const [icpForm, setIcpForm] = useState({ certificate_serial: '', certificate_issuer: '', signature_type: 'CIENCIA' });
  const [icpConfig, setIcpConfig] = useState(null);

  const canAck = ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'CASA_CIVIL', 'GOVERNADOR', 'SECRETARIA_EXECUTORA'].includes(user?.role);

  const fetchAcks = async () => {
    try {
      const res = await api.get(`/acts/${actId}/acknowledgements`);
      setAcks(res.data || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchICPConfig = async () => {
    try {
      const res = await api.get('/signature/config');
      setIcpConfig(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { fetchAcks(); fetchICPConfig(); }, [actId]);

  const handleSubmit = async () => {
    try {
      await api.post(`/acts/${actId}/acknowledge`, form);
      toast.success('Ciência registrada');
      setShowDialog(false);
      fetchAcks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleICPSign = async () => {
    try {
      // Generate a mock signature value (in production, this would come from the certificate)
      const signatureValue = btoa(`${user.name}-${Date.now()}-${actId}`);
      
      await api.post(`/signature/sign/${actId}`, null, {
        params: {
          signature_type: icpForm.signature_type,
          certificate_serial: icpForm.certificate_serial,
          certificate_issuer: icpForm.certificate_issuer,
          signature_value: signatureValue
        }
      });
      toast.success('Assinatura ICP-Brasil registrada');
      setShowICPDialog(false);
      setIcpForm({ certificate_serial: '', certificate_issuer: '', signature_type: 'CIENCIA' });
      fetchAcks();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro na assinatura ICP'); }
  };

  if (loading) return null;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <PenTool className="w-4 h-4" /> Assinaturas / Ciências
        </CardTitle>
      </CardHeader>
      <CardContent>
        {acks.length === 0 ? (
          <p className="text-slate-500 text-sm text-center py-2">Nenhuma ciência registrada</p>
        ) : (
          <div className="space-y-2">
            {acks.map((ack) => (
              <div key={ack.ack_id} className="flex items-center justify-between p-2 bg-slate-50 rounded">
                <div>
                  <p className="font-medium text-sm">{ack.nome}</p>
                  <p className="text-xs text-slate-500">{ack.tipo} via {ack.via}</p>
                </div>
                <Badge variant={ack.via === 'ICP-BRASIL' ? 'default' : 'outline'} className={`text-xs ${ack.via === 'ICP-BRASIL' ? 'bg-green-600' : ''}`}>
                  {ack.via === 'ICP-BRASIL' ? '🔐 ICP' : ''} {new Date(ack.signed_at).toLocaleDateString('pt-BR')}
                </Badge>
              </div>
            ))}
          </div>
        )}
        {canAck && (
          <div className="flex gap-2 mt-3">
            <Button size="sm" variant="outline" className="flex-1" onClick={() => setShowDialog(true)}>
              <CheckCircle className="w-4 h-4 mr-1" /> Ciência Simples
            </Button>
            <Button size="sm" className="flex-1 bg-green-600 hover:bg-green-700" onClick={() => setShowICPDialog(true)}>
              <Shield className="w-4 h-4 mr-1" /> ICP-Brasil
            </Button>
          </div>
        )}

        {/* Simple Acknowledgement Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Registrar Ciência</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Tipo</Label>
                <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CIENCIA">Ciência</SelectItem>
                    <SelectItem value="APROVACAO">Aprovação</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Via de Assinatura</Label>
                <Select value={form.via} onValueChange={(v) => setForm({ ...form, via: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CREDENCIAL_INTERNA">Credencial Interna</SelectItem>
                    <SelectItem value="GOVBR">Gov.BR</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>CPF (opcional)</Label>
                <Input value={form.cpf} onChange={(e) => setForm({ ...form, cpf: e.target.value })} placeholder="000.000.000-00" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit}>Registrar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* ICP-Brasil Dialog */}
        <Dialog open={showICPDialog} onOpenChange={setShowICPDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-green-600" />
                Assinatura Digital ICP-Brasil
              </DialogTitle>
              <DialogDescription>
                Assinatura com validade jurídica conforme MP 2.200-2/2001
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              {icpConfig && !icpConfig.crypto_available && (
                <div className="p-3 bg-amber-50 rounded-lg text-amber-800 text-sm">
                  Módulo de criptografia não disponível no servidor.
                </div>
              )}
              <div>
                <Label>Tipo de Assinatura</Label>
                <Select value={icpForm.signature_type} onValueChange={(v) => setIcpForm({ ...icpForm, signature_type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="CIENCIA">Ciência</SelectItem>
                    <SelectItem value="APROVACAO">Aprovação</SelectItem>
                    <SelectItem value="ASSINATURA_DOCUMENTO">Assinatura de Documento</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Número de Série do Certificado</Label>
                <Input 
                  value={icpForm.certificate_serial} 
                  onChange={(e) => setIcpForm({ ...icpForm, certificate_serial: e.target.value })} 
                  placeholder="Ex: 1234567890ABCDEF"
                />
              </div>
              <div>
                <Label>Emissor do Certificado (AC)</Label>
                <Input 
                  value={icpForm.certificate_issuer} 
                  onChange={(e) => setIcpForm({ ...icpForm, certificate_issuer: e.target.value })} 
                  placeholder="Ex: AC SERASA SSL EV"
                />
              </div>
              <div className="p-3 bg-blue-50 rounded-lg text-blue-800 text-sm">
                <p className="font-medium">Certificados suportados:</p>
                <p>• A1 (software) - Arquivo .pfx ou .p12</p>
                <p>• A3 (token/smartcard) - Via leitor de cartão</p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowICPDialog(false)}>Cancelar</Button>
              <Button 
                onClick={handleICPSign} 
                disabled={!icpForm.certificate_serial || !icpForm.certificate_issuer}
                className="bg-green-600 hover:bg-green-700"
              >
                <Shield className="w-4 h-4 mr-1" /> Assinar
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// ========== RISK PROFILE ==========
export const RiskProfileSection = ({ actId }) => {
  const { api, user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [form, setForm] = useState({ nivel: 'MEDIO', impacto: 'MISTO', probabilidade: 'MEDIA', fonte: '' });

  const canEdit = ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'CGE', 'CASA_CIVIL', 'CONTROLADORIA_INTERNA'].includes(user?.role);

  const fetchProfile = async () => {
    try {
      const res = await api.get(`/acts/${actId}/risk-profile`);
      setProfile(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchProfile(); }, [actId]);

  const handleSubmit = async () => {
    try {
      await api.post(`/acts/${actId}/risk-profile`, form);
      toast.success('Perfil de risco atualizado');
      setShowDialog(false);
      fetchProfile();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  const handleGenerateAI = async () => {
    setGenerating(true);
    try {
      const res = await api.post(`/ai/engine/risk-profile/${actId}`);
      toast.success('Perfil de risco gerado pela IA');
      setProfile(res.data);
    } catch (e) { toast.error('Erro ao gerar perfil'); }
    finally { setGenerating(false); }
  };

  const NIVEL_COLORS = { BAIXO: 'bg-green-100 text-green-800', MEDIO: 'bg-yellow-100 text-yellow-800', ALTO: 'bg-orange-100 text-orange-800', CRITICO: 'bg-red-100 text-red-800' };

  if (loading) return null;

  return (
    <Card className={profile?.nivel === 'CRITICO' ? 'border-l-4 border-l-red-500' : profile?.nivel === 'ALTO' ? 'border-l-4 border-l-orange-500' : ''}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertTriangle className="w-4 h-4" /> Risco & Impacto
        </CardTitle>
      </CardHeader>
      <CardContent>
        {profile ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-sm text-slate-600">Nível:</span>
              <Badge className={NIVEL_COLORS[profile.nivel]}>{profile.nivel}</Badge>
            </div>
            <p className="text-sm"><strong>Impacto:</strong> {profile.impacto}</p>
            <p className="text-sm"><strong>Probabilidade:</strong> {profile.probabilidade}</p>
            {profile.justificativa && <p className="text-sm text-slate-600 italic">{profile.justificativa}</p>}
            <p className="text-xs text-slate-500">Fonte: {profile.fonte}</p>
            {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => { setForm(profile); setShowDialog(true); }}>Editar</Button>}
          </div>
        ) : (
          <div className="text-center py-4 space-y-2">
            <p className="text-slate-500 text-sm">Perfil de risco não definido</p>
            {canEdit && (
              <div className="flex gap-2 justify-center">
                <Button size="sm" variant="outline" onClick={() => setShowDialog(true)}>Definir Manual</Button>
                <Button size="sm" onClick={handleGenerateAI} disabled={generating}>
                  {generating ? 'Gerando...' : 'Gerar com IA'}
                </Button>
              </div>
            )}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Definir Perfil de Risco</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Nível de Risco</Label>
                <Select value={form.nivel} onValueChange={(v) => setForm({ ...form, nivel: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BAIXO">Baixo</SelectItem>
                    <SelectItem value="MEDIO">Médio</SelectItem>
                    <SelectItem value="ALTO">Alto</SelectItem>
                    <SelectItem value="CRITICO">Crítico</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Tipo de Impacto</Label>
                <Select value={form.impacto} onValueChange={(v) => setForm({ ...form, impacto: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FINANCEIRO">Financeiro</SelectItem>
                    <SelectItem value="REPUTACIONAL">Reputacional</SelectItem>
                    <SelectItem value="JURIDICO">Jurídico</SelectItem>
                    <SelectItem value="OPERACIONAL">Operacional</SelectItem>
                    <SelectItem value="MISTO">Misto</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Probabilidade</Label>
                <Select value={form.probabilidade} onValueChange={(v) => setForm({ ...form, probabilidade: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BAIXA">Baixa</SelectItem>
                    <SelectItem value="MEDIA">Média</SelectItem>
                    <SelectItem value="ALTA">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Fonte/Observação</Label>
                <Input value={form.fonte} onChange={(e) => setForm({ ...form, fonte: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};

// ========== CUSTO DO NÃO CUMPRIMENTO ==========
export const CostImpactSection = ({ actId }) => {
  const { api, user } = useAuth();
  const [impact, setImpact] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({ estimativa_monetaria: '', descricao_impacto: '', fonte_referencia: '' });

  const canEdit = ['ADMIN_ESTADUAL', 'GESTOR_GERAL', 'CGE', 'CASA_CIVIL', 'CONTROLADORIA_INTERNA'].includes(user?.role);

  const fetchImpact = async () => {
    try {
      const res = await api.get(`/acts/${actId}/cost-impact`);
      setImpact(res.data);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchImpact(); }, [actId]);

  const handleSubmit = async () => {
    try {
      await api.post(`/acts/${actId}/cost-impact`, {
        ...form,
        estimativa_monetaria: form.estimativa_monetaria ? parseFloat(form.estimativa_monetaria) : null
      });
      toast.success('Impacto atualizado');
      setShowDialog(false);
      fetchImpact();
    } catch (e) { toast.error(e.response?.data?.detail || 'Erro'); }
  };

  if (loading) return null;

  const formatCurrency = (value) => {
    if (!value) return '-';
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <DollarSign className="w-4 h-4" /> Impacto do Não Cumprimento
        </CardTitle>
      </CardHeader>
      <CardContent>
        {impact ? (
          <div className="space-y-2">
            {impact.estimativa_monetaria && (
              <p className="text-2xl font-bold text-red-600">{formatCurrency(impact.estimativa_monetaria)}</p>
            )}
            <p className="text-sm">{impact.descricao_impacto}</p>
            {impact.fonte_referencia && <p className="text-xs text-slate-500">Fonte: {impact.fonte_referencia}</p>}
            {canEdit && <Button size="sm" variant="outline" className="mt-2" onClick={() => { setForm({ ...impact, estimativa_monetaria: impact.estimativa_monetaria?.toString() || '' }); setShowDialog(true); }}>Editar</Button>}
          </div>
        ) : (
          <div className="text-center py-4">
            <p className="text-slate-500 text-sm mb-2">Impacto não definido</p>
            {canEdit && <Button size="sm" onClick={() => setShowDialog(true)}>Definir Impacto</Button>}
          </div>
        )}

        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Impacto do Não Cumprimento</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Estimativa Monetária (R$)</Label>
                <Input type="number" value={form.estimativa_monetaria} onChange={(e) => setForm({ ...form, estimativa_monetaria: e.target.value })} placeholder="0.00" />
              </div>
              <div>
                <Label>Descrição do Impacto</Label>
                <Textarea value={form.descricao_impacto} onChange={(e) => setForm({ ...form, descricao_impacto: e.target.value })} placeholder="Descreva os impactos potenciais..." />
              </div>
              <div>
                <Label>Fonte/Referência</Label>
                <Input value={form.fonte_referencia} onChange={(e) => setForm({ ...form, fonte_referencia: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
              <Button onClick={handleSubmit}>Salvar</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
};
